import { defineStore } from 'pinia'
import { computed } from 'vue'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'
import { useAsyncResource } from '../composables/useAsyncResource'

// Read model for the dedicated `/memory` view. Mirrors the
// compact shape `MemoryEntrySerializer.serialize_summary` returns from
// `GET /api/v1/memory` — no `body`, since the view lists entries rather
// than rendering them. The view fetches with `project=all` (the admin
// escape hatch on `Wiki::Memory::Visibility.list`) so it can show the
// full global-vs-project split and per-scope counts in one place, which
// the wiki browser's flat "memory" category bucket couldn't.
//
// Read-only, no channel broadcasts — the view refreshes by calling
// `fetch()` again on route entry, same as the usage dashboard.

export interface MemoryEntry {
  name: string
  title: string
  description: string
  type: string
  project: string
  path: string
  created_at: string | null
  updated_at: string | null
}

// Full shape from `GET /api/v1/memory/:name` — adds the body the list
// payload omits, so the drawer can show an entry's content
// without navigating to the raw wiki page.
export interface MemoryEntryDetail extends MemoryEntry {
  body: string
}

// Fields for creating an entry via the UI → `POST /api/v1/memory`.
export interface MemoryEntryInput {
  name: string
  type: string
  project: string
  description: string
  body: string
}

// One scope section the view renders: the `global` pseudo-project or a
// real project slug, its entries, and the count shown in the header.
export interface MemoryScopeGroup {
  project: string
  label: string
  isGlobal: boolean
  entries: MemoryEntry[]
  count: number
}

// The reserved scope slug for cross-cutting entries — matches the
// backend's `Wiki::Memory::ProjectSlug::GLOBAL`. Kept as one literal so
// the "is this the global bucket?" test has a single home.
export const GLOBAL_PROJECT = 'global'

// Turn a project slug into a human label for scope headers. Slugs
// are cwd-derived (`workspaces-<repo>`, hyphenated, lowercase); strip the
// `workspaces-` prefix when it leaves something, then space + title-case.
// The raw slug stays the group's `project` key for API calls.
export function humanizeProject(slug: string): string {
  const base = slug.replace(/^workspaces-/, '') || slug
  return base
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const useMemoryEntriesStore = defineStore('memoryEntries', () => {
  // The entries/loaded/loading/loadError lifecycle is owned by
  // useAsyncResource — the same abstraction wiki_pages / lint_notes /
  // synthesis_suggestions use, so a future change to the
  // auth/error contract lands in one place instead of drifting here.
  // `initialValue: []` narrows the data ref to Ref<MemoryEntry[]>.
  const {
    data: entries,
    loaded,
    loading,
    error: loadError,
    refresh,
  } = useAsyncResource<MemoryEntry[]>(
    async () => {
      const payload = await apiGet<MemoryEntry[]>('/api/v1/memory?project=all', {
        failureLabel: 'Failed to load memory entries',
      })
      return payload ?? []
    },
    { initialValue: [] },
  )

  // The view drives fetches via `void store.fetch()` (onMounted) and the
  // header Retry button (`@click`), and the CRUD methods refetch after a
  // mutation — all of which relied on the old hand-rolled fetch() not
  // throwing. useAsyncResource's refresh() rethrows non-auth errors, so
  // wrap it to keep that swallow-on-error contract; loadError still
  // surfaces the failure in the view.
  async function fetch(): Promise<void> {
    await refresh().catch(() => {})
  }

  // Entries grouped by scope, Global first then projects alphabetically.
  // Each group's entries are sorted by name so the list is stable across
  // fetches regardless of the API's row order.
  const groups = computed<MemoryScopeGroup[]>(() => {
    const byProject = new Map<string, MemoryEntry[]>()
    for (const entry of entries.value) {
      const list = byProject.get(entry.project) ?? []
      list.push(entry)
      byProject.set(entry.project, list)
    }

    const result: MemoryScopeGroup[] = []
    for (const [project, list] of byProject) {
      const isGlobal = project === GLOBAL_PROJECT
      result.push({
        project,
        label: isGlobal ? 'Global' : humanizeProject(project),
        isGlobal,
        entries: list.toSorted((a, b) => a.name.localeCompare(b.name)),
        count: list.length,
      })
    }

    return result.toSorted((a, b) => {
      if (a.isGlobal !== b.isGlobal) return a.isGlobal ? -1 : 1
      return a.project.localeCompare(b.project)
    })
  })

  const globalCount = computed(() => entries.value.filter((e) => e.project === GLOBAL_PROJECT).length)
  const projectCount = computed(() => entries.value.length - globalCount.value)
  // Distinct non-global project scopes — the "across N projects" figure.
  const projectScopeCount = computed(() => groups.value.filter((g) => !g.isGlobal).length)
  const totalCount = computed(() => entries.value.length)

  const scoped = (name: string, project: string) =>
    `/api/v1/memory/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`

  // Fetch one entry's full shape (incl. body) on demand.
  async function fetchEntry(name: string, project: string): Promise<MemoryEntryDetail> {
    return await apiGet<MemoryEntryDetail>(scoped(name, project), {
      failureLabel: 'Failed to load memory entry',
    })
  }

  // Create / delete / move from the UI. Each re-`fetch()`es the
  // list afterwards (delete optimistically removes first) so scope/path
  // changes from a move land consistently without hand-patching rows.
  async function createEntry(input: MemoryEntryInput): Promise<void> {
    await apiMutate('/api/v1/memory', {
      method: 'POST',
      body: {
        project: input.project,
        memory: { name: input.name, type: input.type, description: input.description, body: input.body },
      },
      failureLabel: 'Failed to create memory entry',
    })
    await fetch()
  }

  async function deleteEntry(name: string, project: string): Promise<void> {
    await apiMutate(scoped(name, project), {
      method: 'DELETE',
      failureLabel: 'Failed to delete memory entry',
      tolerateStatuses: [204],
    })
    entries.value = entries.value.filter((e) => !(e.name === name && e.project === project))
  }

  async function moveEntry(name: string, fromProject: string, toProject: string): Promise<void> {
    await apiMutate(`/api/v1/memory/${encodeURIComponent(name)}/move`, {
      method: 'POST',
      body: { project: fromProject, to_project: toProject },
      failureLabel: 'Failed to move memory entry',
    })
    await fetch()
  }

  return {
    entries,
    loaded,
    loading,
    loadError,
    groups,
    globalCount,
    projectCount,
    projectScopeCount,
    totalCount,
    fetch,
    fetchEntry,
    createEntry,
    deleteEntry,
    moveEntry,
  }
})
